// Cross-store customer order history -- the pure parts.
//
// The customer modal's Orders tab lists a customer's paid receipts from
// EVERY store (waio-portal GET /ht/api/v2/customers/{id}/receipts). Two
// rules follow from that and live here so they can be unit tested
// without React:
//
//   1. A receipt rung at another store is view-only on this register.
//      Every write route on the portal is store-scoped and answers 409
//      `foreign_store_receipt` for it (refund pays out of the wrong till,
//      inventory lands at the wrong store, card refunds must run on the
//      originating merchant account). `isForeignStoreReceipt` is the
//      client-side mirror that hides those controls up front.
//
//   2. "Buy again" must resolve each line against THIS store's catalogue
//      and fail closed. The portal's till-side gates (oversell,
//      inactive / out-of-stock, deleted product) are the backstop;
//      `canBuyAgain` is the same decision made before the cart is touched.

export interface CustomerReceiptRow {
  id: number
  number?: string | null
  timestamp?: string | null
  status?: number | string | null
  on_hold?: boolean
  is_return?: boolean
  source_number?: string | null
  online?: boolean
  is_v2?: boolean
  store_id?: number | null
  store_name?: string | null
  totals?: { total?: number | string | null; taxes?: number | string | null } | null
  total?: number | string | null
}

export interface CustomerOrderEntry {
  id: number
  receipt_number: string
  timestamp: string
  jamount: number
  store_id: number | null
  store_name: string | null
  /** Rung at a store other than the one this register is signed into. */
  is_foreign: boolean
}

/**
 * True when both store ids are known and differ. Unknown on either side
 * is NOT foreign: that is the pre-existing same-store behaviour, and an
 * old portal that omits `store_id` must not strip every action from the
 * ordinary Orders tab. The server re-checks on every write regardless.
 */
export function isForeignStoreReceipt(
  receiptStoreId: number | null | undefined,
  sessionStoreId: number | null | undefined,
): boolean {
  if (receiptStoreId == null || sessionStoreId == null) return false
  return Number(receiptStoreId) !== Number(sessionStoreId)
}

export function toCustomerOrderEntry(
  row: CustomerReceiptRow,
  sessionStoreId: number | null | undefined,
): CustomerOrderEntry {
  const total = Number(row.totals?.total ?? row.total ?? 0)
  // A store_id that isn't a number (row-shape drift, an empty string
  // from an old serializer) has to read as UNKNOWN, not NaN: NaN never
  // equals the session id, so it would mark the row foreign and the
  // Orders-tab pill would render the literal "Store NaN".
  const rawStoreId = row.store_id == null ? null : Number(row.store_id)
  const storeId =
    rawStoreId != null && Number.isFinite(rawStoreId) ? rawStoreId : null
  return {
    id: row.id,
    receipt_number: row.number ?? String(row.id),
    timestamp: row.timestamp ?? '',
    jamount: Number.isFinite(total) ? total : 0,
    store_id: storeId,
    store_name: row.store_name ?? null,
    is_foreign: isForeignStoreReceipt(storeId, sessionStoreId),
  }
}

export interface BuyAgainProductLike {
  on_hand?: number | string | null
  quantity_available?: number | string | null
  quantity?: number | string | null
  price?: number | string | null
  is_inactive?: boolean
  out_of_stock?: boolean
}

export type BuyAgainVerdict =
  | { ok: true }
  | { ok: false; reason: 'missing' | 'inactive' | 'out_of_stock' | 'no_stock' | 'unpriced' }

export interface BuyAgainOptions {
  /**
   * The store's `frontend.hide_out_of_stock` setting (POSConfig
   * `posHideOutOfStock`). The till only refuses the two merchandising
   * flags under this setting -- see canBuyAgain. Default false, which
   * matches an unset SysConfig.
   */
  hideOutOfStock?: boolean
}

/**
 * Fail-closed "can this line go into the cart at this store" check.
 *
 *   missing      -> the product did not resolve at this store (not
 *                   carried here, deleted, or the lookup failed)
 *   inactive     -> per-store Inactive flag, `hideOutOfStock` only
 *   out_of_stock -> per-store manual out-of-stock flag, same
 *   no_stock     -> on-hand unknown or <= 0 (unknown counts as none:
 *                   the old check let 'unknown' through)
 *   unpriced     -> no positive price at this store
 *
 * `is_inactive` / `out_of_stock` are MERCHANDISING flags, not stock:
 * waio-portal's till (ops_receipt.add_item) refuses them only when the
 * store has `frontend.hide_out_of_stock` on -- everywhere else a
 * flagged product still sells over the counter, it is just hidden from
 * the shop. Refusing them unconditionally made "buy again" stricter
 * than the register beside it, so they are honoured only when the
 * caller passes the store's setting.
 */
export function canBuyAgain(
  product: BuyAgainProductLike | null | undefined,
  { hideOutOfStock = false }: BuyAgainOptions = {},
): BuyAgainVerdict {
  if (!product) return { ok: false, reason: 'missing' }
  if (hideOutOfStock) {
    if (product.is_inactive === true) return { ok: false, reason: 'inactive' }
    if (product.out_of_stock === true) return { ok: false, reason: 'out_of_stock' }
  }
  const onHandRaw = product.on_hand ?? product.quantity_available ?? product.quantity ?? null
  const onHand = onHandRaw == null ? NaN : Number(onHandRaw)
  if (!Number.isFinite(onHand) || onHand <= 0) return { ok: false, reason: 'no_stock' }
  const price = product.price == null ? NaN : Number(product.price)
  if (!Number.isFinite(price) || price <= 0) return { ok: false, reason: 'unpriced' }
  return { ok: true }
}

export function describeBuyAgainRefusal(verdict: BuyAgainVerdict): string | null {
  if (verdict.ok) return null
  switch (verdict.reason) {
    case 'missing':
      return 'Not carried at this store'
    case 'inactive':
      return 'Inactive at this store'
    case 'out_of_stock':
      return 'Marked out of stock here'
    case 'no_stock':
      return 'No stock at this store'
    case 'unpriced':
      return 'No price at this store'
  }
}
